/* =====================================================================================
   Glidepath's compute engine and data access, shared between Forecast (the module that
   edits a scenario) and Council (which only ever reads one, to hand a board seat real
   figures instead of whatever the person asking happened to type). Extracted rather than
   duplicated: a correction to how EBITDA or cash is computed has to reach both call sites
   the same day, not whichever one someone remembers to also fix.

   WHAT "EBITDA" AND "CASH" ACTUALLY MEAN HERE (0101). Council's CFO seat's first real
   review found the model treating capex as an ordinary expense (so the line it called
   EBITDA was not EBITDA), assuming every distributor was paid the instant a sale was
   recognised (so cash showed money that was, in reality, sitting in receivables), and
   crediting grants as committed cash with no award letter behind them. Fixed here:
     - Capex (finance.opex_lines.is_capex) is excluded from the opex that reduces EBITDA,
       reduces cash directly in the period spent, and is depreciated straight-line over
       scenarios.capex_useful_life_years as its own EBIT-level charge.
     - Distributor receivables (finance.channels.payment_terms_days) create a working-
       capital drag: cash collected in a period is revenue less the GROWTH in the
       receivables balance that period, not revenue itself.
     - Grants are excluded from both EBITDA and cash while scenarios.grants_committed is
       false (today's true state for Scira: no award letters exist). Once true, they
       enter EBITDA as earned income in the period they relate to, but their CASH is
       pro-rated across that period and the next by grant_lag_days / 365, because real
       grant schemes reimburse in arrears.
   ===================================================================================== */

import { BACKEND } from './db';

export type CaseType = 'base' | 'upside' | 'downside' | 'other';
export type Arrangement = 'direct' | 'distributor' | 'profitshare';

export interface Scenario {
  id: string; org_id: string; name: string; case_type: CaseType;
  currency: string; unit_label: string; notes: string | null;
  years: string[]; grants_by_period: number[]; headcount: number[]; cost_per_head: number[];
  headcount_is_live: boolean;
  capex_useful_life_years: number; grant_lag_days: number; grants_committed: boolean;
}
export interface Channel {
  id: string; scenario_id: string; name: string; arrangement: Arrangement;
  price: number; cash_cost: number; rev_share_pct: number;
  start_idx: number; end_idx: number | null;
  vol_mode: 'manual' | 'growth';
  manual_volumes: number[];
  growth: { base: number; rates: number[] };
  sort_order: number;
  payment_terms_days: number;
}
export interface OpexLine {
  id: string; scenario_id: string; name: string; amounts: number[]; sort_order: number;
  is_capex: boolean;
}
export interface EquityRaise { id: string; scenario_id: string; name: string; period_idx: number; amount: number }

export const CASE_LABEL: Record<CaseType, string> = {
  base: 'Base case', upside: 'Upside', downside: 'Downside', other: 'Other',
};
export const ARR_LABEL: Record<Arrangement, string> = {
  direct: 'Direct sale', distributor: 'Through distributor', profitshare: 'Profit-share / royalty',
};

/* ---------------- compute engine, ported from the standalone Glidepath tool ---------------- */

export function volumeAt(ch: Channel, i: number): number {
  if (i < ch.start_idx || (ch.end_idx != null && i > ch.end_idx)) return 0;
  if (ch.vol_mode === 'manual') return Number(ch.manual_volumes[i]) || 0;
  let v = Number(ch.growth?.base) || 0;
  for (let j = ch.start_idx + 1; j <= i; j++) v *= 1 + (Number(ch.growth?.rates?.[j]) || 0) / 100;
  return Math.round(v);
}

export function computeChannel(ch: Channel, N: number) {
  const rev: number[] = [], cogs: number[] = [], units: number[] = [];
  for (let i = 0; i < N; i++) {
    const u = volumeAt(ch, i);
    const gross = u * ch.price;
    const share = gross * (Number(ch.rev_share_pct) || 0) / 100;
    rev.push(gross - share);
    cogs.push(u * ch.cash_cost);
    units.push(u);
  }
  return { rev, cogs, units };
}

export interface ComputedModel {
  perChannel: Record<string, { rev: number[]; cogs: number[]; units: number[] }>;
  totRev: number[]; totCogs: number[]; gp: number[]; gm: number[];
  peopleCost: number[]; opexTot: number[];
  capex: number[]; depreciation: number[];
  ar: number[]; deltaAR: number[];
  grantsPL: number[]; grantsCash: number[];
  ebitda: number[]; ebit: number[]; equity: number[]; cash: number[];
  minCash: number; minCashIdx: number;
}

export function computeModel(sc: Scenario, channels: Channel[], opex: OpexLine[], equity: EquityRaise[]): ComputedModel {
  const N = sc.years.length;
  const perChannel: ComputedModel['perChannel'] = {};
  channels.forEach((ch) => { perChannel[ch.id] = computeChannel(ch, N); });

  const totRev: number[] = [], totCogs: number[] = [], gp: number[] = [], gm: number[] = [];
  const peopleCost: number[] = [], opexTot: number[] = [], capex: number[] = [];
  const ar: number[] = [], deltaAR: number[] = [];
  const grantsPL: number[] = [], grantsCash: number[] = [];
  const ebitda: number[] = [], ebit: number[] = [];

  const capexLines = opex.filter((o) => o.is_capex);
  const nonCapexLines = opex.filter((o) => !o.is_capex);
  const life = Math.max(1, Number(sc.capex_useful_life_years) || 4);
  const grantLagFrac = Math.min(1, Math.max(0, Number(sc.grant_lag_days) || 0) / 365);

  for (let i = 0; i < N; i++) {
    let r = 0, c = 0;
    channels.forEach((ch) => { r += perChannel[ch.id].rev[i]; c += perChannel[ch.id].cogs[i]; });
    totRev.push(r); totCogs.push(c); gp.push(r - c); gm.push(r ? (r - c) / r : 0);

    const people = (Number(sc.headcount[i]) || 0) * (Number(sc.cost_per_head[i]) || 0);
    peopleCost.push(people);
    let ox = people;
    nonCapexLines.forEach((o) => { ox += Number(o.amounts[i]) || 0; });
    opexTot.push(ox);

    let cx = 0;
    capexLines.forEach((o) => { cx += Number(o.amounts[i]) || 0; });
    capex.push(cx);

    // Accounts receivable: the balance outstanding at period end, per channel's own
    // terms, summed. A channel paid on sale (0 days) never carries a balance.
    let arTotal = 0;
    channels.forEach((ch) => {
      const rev_i = perChannel[ch.id].rev[i];
      arTotal += rev_i * ((Number(ch.payment_terms_days) || 0) / 365);
    });
    ar.push(arTotal);
    deltaAR.push(arTotal - (i > 0 ? ar[i - 1] : 0));

    const grantAward = Number(sc.grants_by_period[i]) || 0;
    const gPL = sc.grants_committed ? grantAward : 0;
    grantsPL.push(gPL);

    ebitda.push((r - c) - ox + gPL);
  }

  // Depreciation: every capex vintage contributes amount/life for `life` years starting
  // the year it was spent. Grant cash: this period's own award, less what rolls into next
  // period, plus whatever rolled in from the period before.
  for (let i = 0; i < N; i++) {
    let dep = 0;
    for (let v = Math.max(0, i - life + 1); v <= i; v++) dep += capex[v] / life;
    ebit.push(ebitda[i] - dep);

    if (sc.grants_committed) {
      const thisAward = Number(sc.grants_by_period[i]) || 0;
      const prevAward = i > 0 ? (Number(sc.grants_by_period[i - 1]) || 0) : 0;
      grantsCash.push(thisAward * (1 - grantLagFrac) + prevAward * grantLagFrac);
    } else {
      grantsCash.push(0);
    }
  }
  // depreciation pushed above inline with ebit; re-derive the standalone array for callers
  // that want it directly (kept separate from ebit so a P&L table can show both rows).
  const depreciation = ebitda.map((e, i) => e - ebit[i]);

  const equityByPeriod = Array(N).fill(0);
  equity.forEach((e) => { if (e.period_idx >= 0 && e.period_idx < N) equityByPeriod[e.period_idx] += Number(e.amount) || 0; });

  const cash: number[] = [];
  let running = 0;
  for (let i = 0; i < N; i++) {
    // Cash movement is never driven by reported EBITDA directly: capex and the AR
    // build are real cash effects EBITDA does not carry, and grant cash timing can
    // differ from when the grant was earned in the P&L.
    const operatingCash = (gp[i] - opexTot[i]) - deltaAR[i];
    running += operatingCash - capex[i] + equityByPeriod[i] + grantsCash[i];
    cash.push(running);
  }

  return {
    perChannel, totRev, totCogs, gp, gm, peopleCost, opexTot, capex, depreciation,
    ar, deltaAR, grantsPL, grantsCash, ebitda, ebit, equity: equityByPeriod, cash,
    minCash: Math.min(...cash), minCashIdx: cash.indexOf(Math.min(...cash)),
  };
}

export function breakEvenYear(sc: Scenario, res: ComputedModel): string {
  for (let i = 0; i < sc.years.length; i++) if (res.ebitda[i] >= 0) return sc.years[i];
  return 'beyond model horizon';
}

/* ---------------- formatting ---------------- */

export function fmt(sc: Scenario, n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: sc.currency || 'GBP', maximumFractionDigits: 0 }).format(n);
}
export function fmtCompact(sc: Scenario, n: number): string {
  const neg = n < 0; const abs = Math.abs(n);
  const symbol = new Intl.NumberFormat('en-GB', { style: 'currency', currency: sc.currency || 'GBP' }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? sc.currency;
  let s: string;
  if (abs >= 1e6) s = (abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  else if (abs >= 1e3) s = (abs / 1e3).toFixed(0) + 'k';
  else s = Math.round(abs).toString();
  return (neg ? '−' : '') + symbol + s;
}

/* ---------------- data access ---------------- */

export async function loadScenarios(): Promise<Scenario[]> {
  if (BACKEND !== 'supabase') return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('finance').from('scenarios').select('*').order('created_at');
  if (error) throw error;
  return (data ?? []) as Scenario[];
}
export async function loadChildren(scenarioId: string) {
  const { supabase } = await import('./supabase');
  const fin = supabase().schema('finance');
  const [{ data: channels, error: e1 }, { data: opex, error: e2 }, { data: equity, error: e3 }] = await Promise.all([
    fin.from('channels').select('*').eq('scenario_id', scenarioId).order('sort_order'),
    fin.from('opex_lines').select('*').eq('scenario_id', scenarioId).order('sort_order'),
    fin.from('equity_raises').select('*').eq('scenario_id', scenarioId).order('period_idx'),
  ]);
  if (e1) throw e1; if (e2) throw e2; if (e3) throw e3;
  return { channels: (channels ?? []) as Channel[], opex: (opex ?? []) as OpexLine[], equity: (equity ?? []) as EquityRaise[] };
}

/* Renders a scenario as plain text a board seat can actually read: every channel, every
   opex line, headcount and equity timing, by period, plus the totals. Numbers are
   computed here (not left for the model to add up across 7 years and 8 channels), so a
   seat is reviewing the plan's real arithmetic, not its own. */
export function summarizeForBoard(sc: Scenario, channels: Channel[], opex: OpexLine[], equity: EquityRaise[]): string {
  const res = computeModel(sc, channels, opex, equity);
  const years = sc.years;
  const row = (label: string, vals: number[], compact = true) =>
    `${label}: ${vals.map((v) => (compact ? fmtCompact(sc, v) : fmt(sc, v))).join(', ')}`;

  const lines: string[] = [];
  lines.push(`Glidepath scenario: "${sc.name}" (${CASE_LABEL[sc.case_type]}), periods ${years.join(', ')}.`);
  if (sc.notes) lines.push(`Notes: ${sc.notes}`);
  lines.push('');
  lines.push('Revenue by channel:');
  channels.forEach((ch) => {
    const r = res.perChannel[ch.id];
    const terms = ch.payment_terms_days > 0 ? `, ${ch.payment_terms_days}-day terms` : ', paid on sale';
    lines.push(`  ${ch.name} (${ARR_LABEL[ch.arrangement]}, price ${fmt(sc, ch.price)}, cost ${fmt(sc, ch.cash_cost)}${terms}): ${row('revenue', r.rev).replace('revenue: ', '')}`);
  });
  lines.push('');
  lines.push(row('Total revenue', res.totRev));
  lines.push(row('Gross profit', res.gp));
  lines.push(row('Accounts receivable (period end)', res.ar));
  lines.push(row('Change in receivables (cash drag when positive)', res.deltaAR));
  lines.push('');
  lines.push('Operating expenses (excludes capex, which is capitalised and depreciated separately):');
  lines.push(`  People (headcount x cost per head): ${res.peopleCost.map((v) => fmtCompact(sc, v)).join(', ')}`);
  lines.push(`  Headcount: ${sc.headcount.join(', ')}`);
  opex.filter((o) => !o.is_capex).forEach((o) => lines.push(`  ${o.name}: ${o.amounts.map((v) => fmtCompact(sc, v)).join(', ')}`));
  lines.push(row('Opex total', res.opexTot));
  lines.push('');
  const capexLines = opex.filter((o) => o.is_capex);
  if (capexLines.length) {
    lines.push(`Capex (capitalised, depreciated straight-line over ${sc.capex_useful_life_years} years):`);
    capexLines.forEach((o) => lines.push(`  ${o.name}: ${o.amounts.map((v) => fmtCompact(sc, v)).join(', ')}`));
    lines.push(row('Depreciation', res.depreciation));
    lines.push('');
  }
  lines.push(sc.grants_committed
    ? row('Grants (committed, in P&L; cash lags by ' + sc.grant_lag_days + ' days)', sc.grants_by_period)
    : `Grants: NOT included in EBITDA or cash. Award letters do not yet exist. For reference only, the amounts under discussion are ${sc.grants_by_period.map((v) => fmtCompact(sc, v)).join(', ')}.`);
  lines.push(row('EBITDA', res.ebitda));
  lines.push(row('EBIT (after depreciation)', res.ebit));
  lines.push(row('Cumulative cash', res.cash));
  lines.push(`Cash low point: ${fmtCompact(sc, res.minCash)}, in ${years[res.minCashIdx]}`);
  lines.push(`Break-even (EBITDA, not EBIT): ${breakEvenYear(sc, res)}`);
  lines.push('');
  lines.push('Equity raised: ' + (equity.length
    ? equity.map((e) => `${e.name} ${fmt(sc, e.amount)} in ${years[e.period_idx] ?? `period ${e.period_idx}`}`).join('; ')
    : 'none recorded'));
  return lines.join('\n');
}
